import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@/components/Icon";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import useBlackjackAdminTable from "@/components/live-blackjack/useBlackjackAdminTable";
import LiveDealerVideo from "@/components/live-blackjack/LiveDealerVideo";
import PlayingCard from "@/components/PlayingCard";
import { LIVE_BLACKJACK_TABLES, SUIT_SYMBOL, decisionLabel } from "@/components/live-blackjack/tableConfig";

const RANKS = ["A","2","3","4","5","6","7","8","9","10","J","Q","K"];
const SUITS = ["S","H","D","C"];

// Decision-label → mockup token color (AD-04 s.dc). Preserves decisionLabel() text 1:1.
function decisionColor(label) {
  switch (label) {
    case "BLACKJACK": return "#00D632";
    case "WIN": return "#00D632";
    case "BUST": case "LOSE": case "SURRENDER": return "#EF4444";
    case "PUSH": return "#a1a1aa";
    case "DECIDING": case "HIT": case "DOUBLE": case "DEALING": return "#00D632";
    default: return "#71717a";
  }
}

function openingTarget(table) {
  const players = (table?.seats || []).filter((s) => s.occupied && s.hands?.some((h) => h.bet > 0));
  const firstPass = players.find((s) => (s.hands?.[0]?.cards?.length || 0) === 0);
  if (firstPass) return firstPass.seat;
  if ((table?.dealer?.length || 0) < 1) return -1;
  const secondPass = players.find((s) => (s.hands?.[0]?.cards?.length || 0) === 1);
  return secondPass ? secondPass.seat : null;
}

function awaitingInstruction(reason, split = false) {
  if (reason === "hit") return "HIT — deal requested card";
  if (reason === "double") return "DOUBLE — deal one card";
  if (reason === "initial" && split) return "SPLIT — deal card to split hand";
  if (reason === "initial") return "DEAL required card";
  return String(reason || "CARD").toUpperCase();
}

function handTargetLabel(table) {
  if (!table) return "Connecting…";
  if (table.status === "dealing") {
    const target = openingTarget(table);
    if (target === -1) return "Deal dealer up-card";
    if (target != null) {
      const seat = table.seats?.[target];
      return `Deal opening card to Seat #${target + 1} · ${seat?.username || ""}`;
    }
    return "Opening deal complete";
  }
  if (table.status === "acting" && table.awaiting) {
    const seat = table.seats?.[table.awaiting.seat];
    return `Card required: Seat #${table.awaiting.seat + 1} · ${seat?.username || ""} · ${awaitingInstruction(table.awaiting.reason, (seat?.hands?.length || 0) > 1)}`;
  }
  if (table.status === "acting") {
    const seat = table.seats?.[table.active_seat];
    return `Waiting on Seat #${(table.active_seat ?? 0) + 1} · ${seat?.username || ""}`;
  }
  if (table.status === "dealer") return "Dealer turn — enter hole card and draws";
  if (table.status === "betting") return "Betting is open";
  if (table.status === "settled") return "Round settled";
  if (table.status === "off") return "Table is OFF";
  return "Waiting to open betting";
}

// OP-01 phase tabs — presentation only, maps table.status → the mockup's 4 phases.
const OP_PHASES = [
  { key: "betting", label: "Betting open", match: (st) => st === "betting" },
  { key: "dealing", label: "Dealing", match: (st) => st === "dealing" },
  { key: "acting", label: "Player decisions", match: (st) => st === "acting" },
  { key: "settling", label: "Settling", match: (st) => st === "dealer" || st === "settled" },
];

// Count occupied seats carrying a bet — drives the "Settle & pay {N} seats" label.
function occupiedBetSeatCount(table) {
  return (table?.seats || []).filter((s) => s.occupied && s.hands?.some((h) => h.bet > 0)).length;
}

// Result line under a seat's grade buttons (dealer/settled phases). Presentation only.
//
// When the operator stages a manual override the server's hand.payout (computed for the
// AUTO-graded result) no longer describes what will be paid, so it must NOT be shown.
// Instead the override's return is derived from the bet + standard table rules (win 1:1,
// blackjack 3:2) and the line is marked as an estimate/override. Without an override the
// settled hand.payout from the server is the real figure. "Returns" = total back to the
// player (stake + profit); "Pays"/"Profit" distinguishes winnings from the returned stake.
function seatPayoutLine(staged, hand) {
  const bet = Number(hand?.bet || 0);
  if (bet <= 0) return null;
  const override = Boolean(staged);
  const result = staged || hand?.result || null;
  if (!result) return null;
  const money = (m) => `$${Number(m).toFixed(2)}`;
  // Estimated TOTAL RETURN for a result under standard rules (stake + winnings).
  const estReturn = (r) => {
    switch (r) {
      case "win": return bet * 2;        // 1:1 → stake back + equal profit
      case "blackjack": return bet * 2.5; // 3:2 → stake back + 1.5× profit
      case "push": return bet;            // stake returned
      case "surrender": return bet / 2;   // half stake returned
      case "lose": return 0;
      default: return 0;
    }
  };
  const suffix = override ? " · override (estimate)" : "";
  switch (result) {
    case "win": {
      // Settled server value is a TOTAL RETURN; estimate uses the same convention.
      const ret = override ? estReturn("win") : Number(hand?.payout != null ? hand.payout : estReturn("win"));
      return { text: `Returns ${money(ret)}${suffix}`, color: "#00D632" };
    }
    case "blackjack": {
      const ret = override ? estReturn("blackjack") : Number(hand?.payout != null ? hand.payout : estReturn("blackjack"));
      return { text: `Returns ${money(ret)}${suffix}`, color: "#00D632" };
    }
    case "push":
      return { text: `Returns ${money(bet)}${suffix}`, color: "#a1a1aa" };
    case "lose":
      return { text: `Loses ${money(bet)}${suffix}`, color: "#71717a" };
    case "surrender":
      return { text: `Returns ${money(bet / 2)}${suffix}`, color: "#71717a" };
    default:
      return null;
  }
}

export default function BlackjackOperator() {
  const { tableId = "main" } = useParams();
  const navigate = useNavigate();
  const { state: table, setState, transport, lastUpdateAt, loadError } = useBlackjackAdminTable(tableId);
  const [rank, setRank] = useState("A");
  const [suit, setSuit] = useState("S");
  const [busy, setBusy] = useState(false);
  const [clockTick, setClockTick] = useState(Date.now());
  const busyRef = useRef(false);
  const [manualTarget, setManualTarget] = useState(null);
  const [seatGrades, setSeatGrades] = useState({}); // staged per-hand overrides {"{seat}-{hand}": win|blackjack|push|lose|surrender}
  const [audit, setAudit] = useState([]);
  const [syncStartedAt, setSyncStartedAt] = useState(null);
  const [syncMeasuredMs, setSyncMeasuredMs] = useState(null);
  const [showPublicCalibration, setShowPublicCalibration] = useState(false);

  const target = useMemo(() => handTargetLabel(table), [table]);

  const mintDealerTvPath = async () => {
    const { data } = await api.post(`/admin/bj/dealer-display-token?table=${tableId}`);
    return `/dealer/blackjack/${tableId}#display_token=${encodeURIComponent(data.display_token)}`;
  };

  const openDealerTv = () => {
    // Admin-protected dealer TV — the route is super-admin only and mints its own scoped
    // token with the admin session, so there is no guessable public link in the URL.
    window.open(`/admin/live-blackjack/${tableId}/dealer-tv`, "_blank", "noopener");
  };

  const copyDealerTvLink = async () => {
    // Legacy token link for a SEPARATE (non-admin) dealer screen that can't log in.
    // Requires a valid 12h token or the page shows an access error.
    try {
      const path = await mintDealerTvPath();
      const url = new URL(path, window.location.origin).toString();
      await navigator.clipboard.writeText(url);
      toast.success("Dealer screen link copied · valid for 12 hours");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not copy dealer display link");
    }
  };
  const autoSettleSeconds = table?.auto_settle_at
    ? Math.max(0, Math.ceil(Number(table.auto_settle_at) - (clockTick / 1000)))
    : null;
  const stateStale = Boolean(lastUpdateAt && clockTick - lastUpdateAt > 10000);

  useEffect(() => {
    const id = setInterval(() => setClockTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);


  useEffect(() => {
    let alive = true;
    const loadAudit = async () => {
      try {
        const { data } = await api.get(`/admin/bj/audit?table=${tableId}&limit=12`);
        if (alive) setAudit(data || []);
      } catch {}
    };
    loadAudit();
    const id = setInterval(loadAudit, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [tableId]);

  // useCallback so the keydown effect's dependency on `action` is honest and the
  // window listener isn't needlessly rebound every render. Behavior unchanged.
  const action = useCallback(async (path, body = {}) => {
    if (busyRef.current) return null;
    busyRef.current = true;
    setBusy(true);
    const commandId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    try {
      const { data } = await api.post(
        `${path}?table=${tableId}`,
        body,
        { headers: { "X-Command-ID": commandId } },
      );
      setState(data);
      return data;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      return null;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [tableId, setState]);

  const startSyncCalibration = () => {
    setShowPublicCalibration(true);
    setSyncMeasuredMs(null);
    setSyncStartedAt(performance.now());
    toast.info("Make a distinct physical motion now, then click Video caught up when it appears in the public feed.");
  };

  const finishSyncCalibration = () => {
    if (syncStartedAt == null) return;
    const measured = Math.max(0, Math.min(30000, Math.round((performance.now() - syncStartedAt) / 250) * 250));
    setSyncMeasuredMs(measured);
    setSyncStartedAt(null);
  };

  const applySyncCalibration = async () => {
    if (!table || syncMeasuredMs == null) return;
    if (!["waiting", "settled", "off"].includes(table.status)) {
      return toast.error("Apply stream sync only while the table is idle/settled/off.");
    }
    try {
      const { data } = await api.post(`/admin/bj/config?table=${tableId}`, {
        name: table.name,
        min_bet: Number(table.min_bet),
        max_bet: Number(table.max_bet),
        stream_url: table.stream_url || "",
        operator_stream_url: table.operator_stream_url || "",
        stream_delay_ms: syncMeasuredMs,
      });
      toast.success(`Player overlay delay updated to ${data.stream_delay_ms} ms`);
      await api.get(`/admin/bj/state?table=${tableId}`).then(({ data: next }) => setState(next));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not apply stream delay");
    }
  };

  const targetSeat = () => {
    if (!table) return null;
    if (table.status === "dealing") {
      if (manualTarget === -1 && (table.dealer?.length || 0) < 1) return -1;
      if (manualTarget != null && manualTarget >= 0) {
        const seat = table.seats?.[manualTarget];
        if (seat?.occupied && seat.hands?.some((h) => h.bet > 0) && (seat.hands?.[0]?.cards?.length || 0) < 2) return manualTarget;
      }
      return openingTarget(table);
    }
    if (table.status === "acting" && table.awaiting) return table.awaiting.seat;
    if (table.status === "dealer") return -1;
    return null;
  };

  // useCallback so the keydown effect dep on `addSelectedCard` is honest (matches the
  // `action` memoization above). targetSeat() reads table/manualTarget, so those are deps.
  const addSelectedCard = useCallback(() => {
    if (stateStale) return toast.error("Table state is stale — pause dealing until connection recovers");
    const seat = targetSeat();
    if (seat == null) return toast.error("No card is required right now");
    const pending = action("/admin/bj/card", { rank, suit, seat });
    setManualTarget(null);
    return pending;
  }, [stateStale, action, rank, suit, table, manualTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-seat grade staging (mockup AD-04 WIN / BJ 3:2 / PUSH / LOSE / SURR).
  // Stages a manual override locally keyed "{seat}-{hand}"; committed on "Settle & pay seats".
  const gradeHand = (seatIndex, handIndex, result) => {
    const key = `${seatIndex}-${handIndex}`;
    setSeatGrades((prev) => {
      const next = { ...prev };
      if (next[key] === result) delete next[key]; // toggle off
      else next[key] = result;
      return next;
    });
  };

  // "Settle & pay seats" — commit staged per-seat overrides. "Auto-grade from hands" passes {}.
  const settleSeats = async (useOverrides) => {
    if (stateStale) return toast.error("Table state is stale — pause dealing until connection recovers");
    const results = useOverrides ? seatGrades : {};
    const data = await action("/admin/bj/settle", { results });
    if (data) setSeatGrades({});
    return data;
  };

  useEffect(() => {
    const onKey = (e) => {
      // Ignore modifier combos so browser/OS shortcuts keep working.
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target;
      const tag = (el?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      // Let a focused interactive control (button/link), an open dialog/menu, or any
      // editable region handle Tab/Enter/Space/Backspace itself — only the "canvas"
      // (no interactive element focused) drives the card keypad.
      if (el?.closest?.("button, a, [role=dialog], [role=menu], [contenteditable=''], [contenteditable=true], details[open] summary")) return;
      const k = String(e.key || "").toUpperCase();
      const map = { "1":"A", A:"A", "2":"2","3":"3","4":"4","5":"5","6":"6","7":"7","8":"8","9":"9","0":"10",T:"10",J:"J",Q:"Q",K:"K" };
      if (map[k]) setRank(map[k]);
      else if (SUITS.includes(k)) setSuit(k);
      else if (e.key === "Enter") { e.preventDefault(); addSelectedCard(); }
      else if (e.key === "Backspace") { e.preventDefault(); action("/admin/bj/undo"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rank, suit, addSelectedCard, action]);

  const inDealerPhase = table?.status === "dealer";
  const inSettlePhase = table?.status === "dealer" || table?.status === "settled";
  const dealerCards = table?.dealer || [];
  const dealerValue = table?.dealer_value ?? null;
  const roundNumber = table?.round_number;
  const tableOn = Boolean(table) && table.status !== "off";
  const payableSeatCount = occupiedBetSeatCount(table);
  const streamDelayMs = Number(table?.stream_delay_ms || 0);
  const streamDelaySec = (streamDelayMs / 1000).toFixed(1);
  // OBS/program feed is "live" whenever a feed URL is configured for this table.
  const obsLive = Boolean(table?.operator_stream_url || table?.stream_url);
  const twitchLabel = table?.fallback_stream_url ? "fallback · standby" : "standby";
  const activeTable = LIVE_BLACKJACK_TABLES.find((t) => t.id === tableId);

  const chip = "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-[8px] text-[12px] font-semibold";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="mx-auto max-w-[1760px] p-3 sm:p-4">
        {stateStale && (
          <div className="mb-3 rounded-[14px] border-2 border-[#EF4444]/50 bg-[#EF4444]/15 px-4 py-3 text-center font-black text-[#EF4444]" data-testid="bj-operator-stale">
            TABLE STATE IS STALE — PAUSE PHYSICAL DEALING UNTIL CONNECTION RECOVERS
          </div>
        )}

        <div className="overflow-hidden rounded-[16px] border border-[#1F1F23] bg-[#0A0A0A]" data-screen-label="OP-01 Blackjack operator console">
          {/* ── TOP BAR ─────────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-3 border-b border-[#1F1F23] bg-[#0C0C0E] px-4 py-3 sm:px-6">
            {/* Left cluster */}
            <button
              onClick={() => navigate("/admin")}
              className="inline-flex h-9 items-center gap-1.5 rounded-[9px] px-2.5 text-[13px] font-semibold text-[#a1a1aa] hover:bg-[#141417] hover:text-white"
            >
              <Icon name="arrow-left" size={16} /> Control Room
            </button>
            <span className="hidden h-6 w-px bg-[#1F1F23] sm:block" />

            {/* Table name + Main/VIP segmented switcher */}
            <div className="flex items-center gap-2">
              <span className="font-display text-[17px] font-extrabold tracking-[-.02em]">{table?.name || activeTable?.label || tableId}</span>
              <div className="flex items-center gap-1 rounded-[9px] border border-[#1F1F23] bg-[#141417] p-0.5" data-testid="bj-operator-table-switcher">
                {LIVE_BLACKJACK_TABLES.map((item) => {
                  const activeTab = item.id === tableId;
                  return (
                    <button
                      key={item.id}
                      disabled={busy}
                      onClick={() => navigate(`/admin/live-blackjack/${item.id}/operator`)}
                      className={`rounded-[7px] px-2.5 py-1 text-[12px] font-bold transition disabled:opacity-40 ${
                        activeTab ? "bg-white text-[#0A0A0A]" : "text-[#a1a1aa] hover:text-white"
                      }`}
                    >
                      {item.id === "main" ? "Main" : item.id === "vip" ? "VIP" : item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* LIVE pill (only when table is on) */}
            {tableOn && (
              <span className="inline-flex h-[22px] items-center gap-1.5 rounded-full bg-[#EF4444]/[.12] px-2.5 text-[11px] font-bold tracking-[.06em] text-[#EF4444]">
                <span className={`h-1.5 w-1.5 rounded-full bg-[#EF4444] ${!stateStale ? "animate-pulse" : ""}`} />
                LIVE
              </span>
            )}
            <span className="text-[13px] font-semibold text-[#71717a]">
              {roundNumber != null ? `Round ${Number(roundNumber).toLocaleString()}` : "No round"}
            </span>

            {/* Center: phase tabs */}
            <div className="ml-2 flex items-center gap-1">
              {OP_PHASES.map((p) => {
                const on = table ? p.match(table.status) : false;
                return on ? (
                  <span key={p.key} className="flex h-7 items-center gap-1.5 rounded-full bg-[#00D632] px-2.5 text-[12px] font-bold text-[#0A0A0A]">
                    {p.label}
                  </span>
                ) : (
                  <span key={p.key} className="flex h-7 items-center px-2.5 text-[12px] font-medium text-[#71717a]">
                    {p.label}
                  </span>
                );
              })}
            </div>

            {/* Right cluster: OBS / Twitch / Dealer TV + ⋯ */}
            <div className="ml-auto flex items-center gap-2">
              <span className={`${chip} ${obsLive ? "text-white shadow-[inset_0_0_0_1px_#1F1F23]" : "text-[#71717a] shadow-[inset_0_0_0_1px_#1F1F23]"}`}>
                <span className={`h-[7px] w-[7px] rounded-full ${obsLive ? "bg-[#00D632]" : "bg-[#52525b]"}`} />
                OBS live
              </span>
              <span className={`${chip} text-[#a1a1aa] shadow-[inset_0_0_0_1px_#1F1F23]`}>
                <span className="h-[7px] w-[7px] rounded-full bg-[#52525b]" />
                Twitch {twitchLabel}
              </span>
              <button
                onClick={openDealerTv}
                className={`${chip} bg-[#18181C] text-white shadow-[inset_0_0_0_1px_#26262B] hover:bg-[#1f1f24]`}
              >
                <Icon name="broadcast" size={14} className="text-[#a1a1aa]" /> Dealer TV
              </button>
              <div className="flex items-center gap-1">
                <button
                  onClick={copyDealerTvLink}
                  title="Copy TV link"
                  className="flex h-8 w-8 items-center justify-center rounded-[8px] text-[#71717a] shadow-[inset_0_0_0_1px_#26262B] hover:text-white"
                >
                  <Icon name="copy" size={14} />
                </button>
                <button
                  onClick={openDealerTv}
                  title="Open scoped TV"
                  className="flex h-8 items-center justify-center rounded-[8px] px-2 text-[11px] font-bold text-[#71717a] shadow-[inset_0_0_0_1px_#26262B] hover:text-white"
                >
                  Scoped TV
                </button>
              </div>
            </div>
          </div>

          {/* ── MAIN GRID ───────────────────────────────────────────── */}
          <div className="grid gap-4 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_380px]">
            {/* LEFT: program feed */}
            <div className="flex min-w-0 flex-col gap-3">
              <div className="relative aspect-video w-full overflow-hidden rounded-[14px] border border-[#1F1F23] bg-[#050506]" data-testid="bj-operator-video">
                <LiveDealerVideo config={table?.operator_stream_url ? { url: table.operator_stream_url } : { stream_url: table?.stream_url, video_provider: table?.video_provider, fallback_stream_url: table?.fallback_stream_url }} />

                {/* Top-left overlay: ON AIR + delay chip */}
                <div className="pointer-events-none absolute left-3 top-3 z-30 flex flex-wrap gap-1.5">
                  <span className="flex h-[26px] items-center gap-1.5 rounded-[7px] bg-black/80 px-2.5 text-[11px] font-bold tracking-[.06em] text-[#EF4444]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#EF4444]" /> ON AIR
                  </span>
                  <span className="flex h-[26px] items-center rounded-[7px] bg-black/80 px-2.5 text-[12px] font-semibold text-[#E4E4E7]">
                    Players see this ~{streamDelaySec}s behind
                  </span>
                </div>

                {/* Auto-settle countdown (kept, presentation) */}
                {autoSettleSeconds != null && (
                  <span className="absolute right-3 top-3 z-30 rounded-[7px] bg-black/80 px-2.5 py-1 text-[10px] font-black text-[#00D632]">
                    AUTO PAY {autoSettleSeconds}s · UNDO NOW IF WRONG
                  </span>
                )}

                {/* Bottom-left overlay: DEALER strip */}
                <div className="pointer-events-none absolute bottom-3 left-3 z-30 flex items-end gap-2 rounded-[10px] bg-black/80 px-3 py-2.5">
                  <span className="self-center text-[11px] font-semibold tracking-[.1em] text-[#a1a1aa]">DEALER</span>
                  {dealerCards.length ? (
                    dealerCards.map((card, idx) => (
                      <PlayingCard key={idx} card={card} size="xs" faceDown={card.rank === "?"} />
                    ))
                  ) : (
                    <span className="text-[12px] font-semibold text-[#52525b]">—</span>
                  )}
                  <span className="self-center pl-1 font-display text-[24px] font-extrabold leading-none">{dealerValue != null && dealerCards.length ? dealerValue : "—"}</span>
                </div>
              </div>

              {/* Status strip below the video */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-[14px] border border-[#1F1F23] bg-[#111114] px-4 py-3">
                <div>
                  <div className="text-[12px] font-medium text-[#71717a]">OBS ingest</div>
                  <div className="mt-0.5 text-[14px] font-semibold">{obsLive ? "Connected" : "Offline"}</div>
                </div>
                <div>
                  <div className="text-[12px] font-medium text-[#71717a]">Twitch fallback</div>
                  <div className="mt-0.5 text-[14px] font-semibold">{table?.fallback_stream_url || "standby"}</div>
                </div>
                <div>
                  <div className="text-[12px] font-medium text-[#71717a]">Overlay sync</div>
                  <div className="mt-0.5 flex items-center gap-2 font-display text-[14px] font-bold">
                    <button
                      onClick={startSyncCalibration}
                      disabled={!table?.stream_url || syncStartedAt != null}
                      title="Start sync mark (nudge earlier)"
                      className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] bg-[#18181C] text-[14px] font-bold shadow-[inset_0_0_0_1px_#26262B] hover:bg-[#1f1f24] disabled:opacity-40"
                    >
                      −
                    </button>
                    <span>{streamDelaySec}s</span>
                    <button
                      onClick={finishSyncCalibration}
                      disabled={syncStartedAt == null}
                      title="Video caught up (nudge later)"
                      className="flex h-[26px] w-[26px] items-center justify-center rounded-[7px] bg-[#18181C] text-[14px] font-bold shadow-[inset_0_0_0_1px_#26262B] hover:bg-[#1f1f24] disabled:opacity-40"
                    >
                      +
                    </button>
                  </div>
                </div>
                <button
                  onClick={applySyncCalibration}
                  disabled={syncMeasuredMs == null || busy}
                  className="ml-auto flex h-9 items-center gap-1.5 rounded-[9px] bg-[#18181C] px-3 text-[13px] font-semibold shadow-[inset_0_0_0_1px_#26262B] hover:bg-[#1f1f24] disabled:opacity-40"
                >
                  <Icon name="refresh" size={14} className="text-[#a1a1aa]" /> Resync overlay
                </button>
              </div>

              {/* Collapsible: full stream-sync calibration UI (kept fully wired) */}
              <details className="rounded-[14px] border border-[#1F1F23] bg-[#111114]" data-testid="bj-stream-sync-calibration">
                <summary className="cursor-pointer list-none px-4 py-3 text-[10px] font-black uppercase tracking-[0.25em] text-[#71717a]">
                  Public stream sync calibration
                </summary>
                <div className="px-4 pb-4">
                  <p className="max-w-3xl text-xs text-[#52525b]">
                    Use this before opening a round. Make a distinct physical motion at the table and click Start at the same instant.
                    Watch the public player feed, then click Video caught up when that motion appears. PRYMEBET rounds to the nearest 250 ms.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button disabled={!table?.stream_url || syncStartedAt != null} onClick={startSyncCalibration} variant="outline">
                      Start sync mark
                    </Button>
                    <Button disabled={syncStartedAt == null} onClick={finishSyncCalibration} className="bg-[#E4E4E7] font-black text-black hover:bg-[#E4E4E7]">
                      Video caught up
                    </Button>
                    <Button disabled={syncMeasuredMs == null || busy} onClick={applySyncCalibration} className="bg-[#00D632] font-black text-black hover:bg-[#00c02d]">
                      Apply {syncMeasuredMs == null ? "measured delay" : `${syncMeasuredMs} ms`}
                    </Button>
                  </div>
                  {(showPublicCalibration || syncStartedAt != null || syncMeasuredMs != null) && table?.stream_url && (
                    <div className="mt-3 grid gap-3 lg:grid-cols-[1fr_220px]">
                      <div className="relative h-44 overflow-hidden rounded-xl border border-[#1F1F23] bg-black sm:h-56">
                        <LiveDealerVideo config={{ stream_url: table.stream_url, video_provider: table.video_provider, fallback_stream_url: table.fallback_stream_url }} />
                      </div>
                      <div className="flex flex-col justify-center rounded-xl bg-black/25 p-4 text-center">
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#71717a]">Measured latency</p>
                        <p className="mt-2 text-4xl font-black text-[#00D632]">
                          {syncStartedAt != null ? `${Math.max(0, Math.round(performance.now() - syncStartedAt))} ms` : syncMeasuredMs != null ? `${syncMeasuredMs} ms` : "—"}
                        </p>
                        <p className="mt-2 text-[10px] leading-relaxed text-[#52525b]">
                          Current configured delay: {streamDelayMs} ms.
                          Re-run calibration after changing streaming provider/latency mode.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </details>
            </div>

            {/* RIGHT: controls panel */}
            <div className="flex flex-col gap-4 rounded-[14px] border border-[#1F1F23] bg-[#0C0C0E] p-4">
              {/* DEALER HAND */}
              <div className="flex items-center">
                <span className="text-[12px] font-bold uppercase tracking-[.1em] text-[#71717a]">Dealer hand</span>
                <span className="ml-auto text-[12px] font-semibold text-[#a1a1aa]">Dealer stands on 17</span>
              </div>

              <div className="flex items-center gap-2">
                {dealerCards.map((card, idx) => (
                  <PlayingCard key={idx} card={card} size="md" faceDown={card.rank === "?"} />
                ))}
                <div className="flex h-[74px] w-[52px] items-center justify-center rounded-[8px] border-[1.5px] border-dashed border-[#3F3F46] font-display text-[20px] font-extrabold text-[#52525b]">+</div>
                <span className="ml-auto font-display text-[44px] font-extrabold leading-none">{dealerValue != null && dealerCards.length ? dealerValue : "—"}</span>
              </div>

              {/* Selected card readout */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#52525b]">Selected</span>
                <span className={`rounded-md bg-[#18181C] px-2 py-1 font-display text-base font-extrabold ${suit === "H" || suit === "D" ? "text-[#EF4444]" : "text-white"}`}>{rank}{SUIT_SYMBOL[suit]}</span>
              </div>

              {/* Rank keypad (7-col) + backspace */}
              <div className="grid grid-cols-7 gap-1">
                {RANKS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRank(r)}
                    className={`flex h-9 items-center justify-center rounded-[8px] font-display text-[13px] font-bold shadow-[inset_0_0_0_1px_#26262B] ${rank === r ? "bg-[#00D632] text-[#0A0A0A] shadow-none" : "bg-[#18181C] text-white hover:bg-[#1F1F23]"}`}
                  >
                    {r}
                  </button>
                ))}
                <button
                  onClick={() => action("/admin/bj/undo")}
                  disabled={busy || stateStale}
                  title="Backspace — undo last card"
                  className="flex h-9 items-center justify-center rounded-[8px] bg-[#1C1C21] text-[#a1a1aa] hover:bg-[#26262B] disabled:opacity-50"
                >
                  <Icon name="refresh" size={16} />
                </button>
              </div>

              {/* Suit row */}
              <div className="grid grid-cols-4 gap-1">
                {SUITS.map((s) => {
                  const red = s === "H" || s === "D";
                  return (
                    <button
                      key={s}
                      onClick={() => setSuit(s)}
                      className={`flex h-9 items-center justify-center rounded-[8px] text-[16px] font-bold shadow-[inset_0_0_0_1px_#26262B] ${suit === s ? "bg-[#00D632] text-[#0A0A0A] shadow-none" : `bg-[#18181C] ${red ? "text-[#EF4444]" : "text-white"} hover:bg-[#1F1F23]`}`}
                    >
                      {SUIT_SYMBOL[s]}
                    </button>
                  );
                })}
              </div>

              {/* Add / hole-card entry */}
              <button
                disabled={busy || stateStale}
                onClick={addSelectedCard}
                data-testid="bj-operator-add-card"
                className="flex h-[42px] items-center justify-center rounded-[10px] bg-[#00D632] font-display text-[13px] font-extrabold text-[#0A0A0A] hover:bg-[#00c02d] disabled:opacity-50"
              >
                {inDealerPhase ? "Reveal hole card" : `Add ${rank}${SUIT_SYMBOL[suit]}`}
              </button>
              <p className="text-[11px] leading-snug text-[#52525b]">Rank + suit, then Enter. Backspace undoes last card.</p>

              <div className="h-px bg-[#1F1F23]" />

              {/* ROUND CONTROLS */}
              <span className="text-[12px] font-bold uppercase tracking-[.1em] text-[#71717a]">Round controls</span>
              <div className="grid grid-cols-2 gap-2">
                <button
                  disabled={busy || stateStale}
                  onClick={() => action("/admin/bj/open")}
                  className="flex h-[42px] items-center justify-center rounded-[10px] bg-[#18181C] text-[14px] font-semibold shadow-[inset_0_0_0_1px_#26262B] hover:bg-[#1f1f24] disabled:opacity-50"
                >
                  Open betting
                </button>
                <button
                  disabled={busy || stateStale}
                  onClick={() => action("/admin/bj/deal")}
                  className="flex h-[42px] items-center justify-center rounded-[10px] bg-[#18181C] text-[14px] font-semibold shadow-[inset_0_0_0_1px_#26262B] hover:bg-[#1f1f24] disabled:opacity-50"
                >
                  Deal
                </button>
                <button
                  disabled={busy || stateStale}
                  onClick={() => settleSeats(false)}
                  data-testid="bj-operator-auto-grade"
                  className="flex h-[42px] items-center justify-center rounded-[10px] bg-[#18181C] text-[14px] font-semibold text-white shadow-[inset_0_0_0_1px_#26262B] hover:bg-[#1f1f24] disabled:opacity-50"
                >
                  Auto-grade from hands
                </button>
                <button
                  disabled={busy || stateStale}
                  onClick={() => action("/admin/bj/reset")}
                  data-testid="bj-operator-refund"
                  className="flex h-[42px] items-center justify-center rounded-[10px] text-[14px] font-semibold text-[#EF4444] shadow-[inset_0_0_0_1px_rgba(239,68,68,.45)] hover:bg-[#EF4444]/[.08] disabled:opacity-50"
                >
                  Refund round
                </button>
              </div>

              <button
                disabled={busy || stateStale || table?.status !== "dealer"}
                onClick={() => settleSeats(true)}
                data-testid="bj-operator-settle"
                className="flex h-[52px] items-center justify-center rounded-[12px] bg-[#00D632] font-display text-[16px] font-extrabold text-[#0A0A0A] hover:bg-[#00c02d] disabled:opacity-50"
              >
                Settle &amp; pay {payableSeatCount} seats
              </button>

              {/* Secondary / real controls (kept, none removed) */}
              <details className="rounded-[10px] shadow-[inset_0_0_0_1px_#1F1F23]">
                <summary className="cursor-pointer list-none px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-[#71717a]">⋯ more controls</summary>
                <div className="grid grid-cols-2 gap-2 p-3 pt-1">
                  {table?.status === "off"
                    ? <Button disabled={busy || stateStale} onClick={() => action("/admin/bj/on")} variant="outline">Table ON</Button>
                    : <Button disabled={busy || stateStale} onClick={() => action("/admin/bj/off")} variant="outline">Table OFF</Button>}
                  <Button disabled={busy || stateStale || table?.status !== "dealer"} onClick={() => action("/admin/bj/settle", { results: {} })} variant="outline">Settle by cards</Button>
                  <Button disabled={busy} onClick={openDealerTv} variant="outline">Open scoped TV</Button>
                  <Button disabled={busy} onClick={copyDealerTvLink} variant="outline">Copy TV link</Button>
                </div>
              </details>

              {/* Next required action */}
              <div className="rounded-[10px] bg-[#111114] px-3 py-2.5 shadow-[inset_0_0_0_1px_#1F1F23]">
                <p className="text-[10px] uppercase tracking-widest text-[#71717a]">Next required action</p>
                <p className="mt-1 text-[15px] font-black text-[#00D632]">{target}</p>
                <div className="mt-1.5 flex items-center gap-1.5 text-[11px] font-bold text-[#71717a]">
                  {transport === "realtime" && !stateStale
                    ? <Icon name="wifi" size={14} className="text-[#00D632]" />
                    : <Icon name="wifi" size={14} className={`${stateStale ? "text-[#EF4444]" : "text-[#a1a1aa]"}`} />}
                  {stateStale ? "stale" : transport}
                </div>
              </div>

              {/* Opening-deal target picker (only during dealing) */}
              {table?.status === "dealing" && (
                <div className="rounded-[10px] bg-[#111114] p-3 shadow-[inset_0_0_0_1px_#1F1F23]">
                  <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-[#71717a]">Opening-deal target · one card around, dealer up-card, then second card around</p>
                  <div className="flex flex-wrap gap-1.5">
                    {table.seats?.filter((seat) => seat.occupied && seat.hands?.some((h) => h.bet > 0)).map((seat) => {
                      const count = seat.hands?.[0]?.cards?.length || 0;
                      const selected = manualTarget === seat.seat;
                      return <button key={seat.seat} disabled={count >= 2} onClick={() => setManualTarget(seat.seat)} className={`rounded-[10px] px-3 py-1.5 text-xs font-bold ${selected ? "bg-[#00D632] text-[#0A0A0A]" : "border border-[#1F1F23] bg-[#141417] text-white hover:bg-[#1a1a1e]"} disabled:opacity-30`}>Seat #{seat.seat + 1} · {count}/2</button>;
                    })}
                    <button disabled={(table.dealer?.length || 0) >= 1} onClick={() => setManualTarget(-1)} className={`rounded-[10px] px-3 py-1.5 text-xs font-bold ${manualTarget === -1 ? "bg-white text-[#0A0A0A]" : "border border-[#1F1F23] bg-[#141417] text-white hover:bg-[#1a1a1e]"} disabled:opacity-30`}>Dealer up · {table.dealer?.length || 0}/1</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── BOTTOM: 7 seats in one row ───────────────────────────── */}
          {table ? (
            <div className="grid grid-cols-2 border-t border-[#1F1F23] bg-[#0C0C0E] sm:grid-cols-4 lg:grid-cols-7">
              {table.seats?.map((seat) => {
                const active = table.status === "acting" && table.active_seat === seat.seat;
                const awaiting = table.awaiting?.seat === seat.seat ? table.awaiting.reason : null;
                const borderCol = active ? "#00D632" : "#1F1F23";
                if (!seat.occupied) {
                  return (
                    <div key={seat.seat} className="flex flex-col gap-1.5 border-l px-4 py-3.5" style={{ borderColor: borderCol }}>
                      <div className="text-[11px] font-semibold uppercase text-[#71717a]">Seat {seat.seat + 1}</div>
                      <div className="flex min-h-[92px] items-center justify-center rounded-[8px] border border-dashed border-[#26262B] text-[12px] font-semibold text-[#52525b]">Open seat</div>
                    </div>
                  );
                }
                return (
                  <div key={seat.seat} className="flex flex-col gap-2 border-l px-4 py-3.5" style={{ borderColor: borderCol }}>
                    {seat.hands?.map((hand, hi) => {
                      const isActive = active && (table.active_hand || 0) === hi && !table.awaiting;
                      const reason = awaiting && (table.awaiting.hand || 0) === hi ? awaiting : null;
                      const dLabel = decisionLabel(hand, isActive, reason);
                      const gradeKey = `${seat.seat}-${hi}`;
                      const staged = seatGrades[gradeKey];
                      const sideBets = { ...(seat.side_bets || {}), ...(hand.side_bets || {}) };
                      const sideEntries = Object.entries(sideBets);
                      const sideTag = sideEntries.length
                        ? sideEntries.map(([k, v]) => `${k.toUpperCase()} $${Number(v).toFixed(2)}`).join(" · ")
                        : "—";
                      const bigVal = dLabel === "BUST" ? "Bust" : dLabel === "BLACKJACK" ? "BJ" : (hand.cards?.length ? hand.value : "—");
                      const bigValColor = dLabel === "BUST" ? "#EF4444" : dLabel === "BLACKJACK" ? "#00D632" : "#E4E4E7";
                      const payLine = inSettlePhase ? seatPayoutLine(staged, hand) : null;
                      const opt = (result, label, selBg, selFg) => {
                        const sel = staged === result;
                        return (
                          <button
                            key={result}
                            onClick={() => gradeHand(seat.seat, hi, result)}
                            data-testid={`bj-grade-${result === "blackjack" ? "bj" : result === "surrender" ? "surr" : result}-${seat.seat}-${hi}`}
                            className="flex h-[30px] items-center justify-center rounded-[7px] font-display text-[11px] tracking-[.04em]"
                            style={sel
                              ? { background: selBg, color: selFg, fontWeight: 800 }
                              : { background: "transparent", color: "#71717a", fontWeight: 700, boxShadow: "inset 0 0 0 1px #26262B" }}
                          >
                            {label}
                          </button>
                        );
                      };
                      return (
                        <div key={hi} className="flex flex-col gap-1.5">
                          {/* header: SEAT n + side-bet tag */}
                          <div className="flex items-center justify-between text-[11px] font-semibold uppercase text-[#71717a]">
                            <span>Seat {seat.seat + 1}{seat.hands.length > 1 ? ` · H${hi + 1}` : ""}</span>
                            <span className="truncate pl-2 text-right normal-case">{sideTag}</span>
                          </div>
                          {/* username + big hand value */}
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="truncate text-[13px] font-semibold text-[#E4E4E7]">
                              {seat.username}
                              {seat.mode === "dealer" && <span className="ml-1 rounded bg-[#00D632]/20 px-1 text-[8px] font-black uppercase tracking-wide text-[#00D632]">Auto</span>}
                            </span>
                            <span className="font-display text-[22px] font-extrabold leading-none" style={{ color: bigValColor }}>{bigVal}</span>
                          </div>
                          {/* cards + bet */}
                          <div className="flex items-end justify-between gap-2">
                            <div className="flex flex-wrap items-end gap-1">
                              {hand.cards?.length ? hand.cards.map((card, idx) => (
                                <PlayingCard key={idx} card={card} size="xs" faceDown={card.rank === "?"} />
                              )) : <span className="text-[12px] text-[#52525b]">—</span>}
                            </div>
                            <span className="shrink-0 text-[12px] font-semibold text-[#a1a1aa]">{hand.bet > 0 ? `$${Number(hand.bet).toFixed(2)}` : "—"}</span>
                          </div>
                          {/* staged decision (matches decisionLabel token color) */}
                          <div className="text-right text-[10px] font-bold uppercase" style={{ color: decisionColor(dLabel) }}>{dLabel}</div>
                          {/* grade buttons */}
                          <div className="mt-0.5 grid grid-cols-2 gap-1" data-testid={`bj-seat-grade-${seat.seat}-${hi}`}>
                            {opt("win", "WIN", "#00D632", "#0A0A0A")}
                            {opt("blackjack", "BJ 3:2", "#E4E4E7", "#0A0A0A")}
                            {opt("push", "PUSH", "#52525B", "#ffffff")}
                            {opt("lose", "LOSE", "#EF4444", "#ffffff")}
                            <div className="col-span-2">{opt("surrender", "SURR", "#26262B", "#ffffff")}</div>
                          </div>
                          {/* result line */}
                          {payLine && <span className="text-[12px] font-semibold" style={{ color: payLine.color }}>{payLine.text}</span>}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ) : loadError ? (
            // First-load failure (not the normal connecting state) — show the real
            // reason and a retry so the operator isn't stuck on an endless spinner.
            <div className="flex min-h-[140px] flex-col items-center justify-center gap-2 border-t border-[#1F1F23] bg-[#0C0C0E] text-center" data-testid="bj-operator-load-error">
              <Icon name="wifi" size={22} className="text-[#EF4444]" />
              <p className="text-[13px] font-semibold text-[#EF4444]">{loadError}</p>
              <button
                type="button"
                onClick={() => api.get(`/admin/bj/state?table=${tableId}`).then(({ data }) => setState(data)).catch(() => {})}
                className="rounded-[9px] bg-[#18181C] px-3 py-1.5 text-[12px] font-bold text-white shadow-[inset_0_0_0_1px_#26262B] hover:bg-[#1f1f24]"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="flex min-h-[140px] flex-col items-center justify-center gap-2 border-t border-[#1F1F23] bg-[#0C0C0E] text-center">
              <Icon name="wifi" size={22} className="text-[#52525b]" />
              <p className="text-[13px] font-semibold text-[#71717a]">Waiting for round — connecting to table…</p>
            </div>
          )}
        </div>

        {/* ── Audit trail (below the console) ─────────────────────── */}
        <div className="mt-4 rounded-[14px] border border-[#1F1F23] bg-[#111114] p-4" data-testid="bj-operator-audit">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#71717a]">Recent operator activity</p>
              <p className="text-xs text-[#52525b]">Durable audit trail · newest first</p>
            </div>
            <span className="rounded-full bg-white/5 px-2 py-1 text-[10px] font-bold text-[#71717a]">{audit.length} events</span>
          </div>
          <div className="space-y-1.5">
            {audit.length ? audit.slice(0, 8).map((row) => {
              const p = row.payload || {};
              const detail = row.event === "card_entered"
                ? `${p.seat === -1 ? "Dealer" : `Seat #${Number(p.seat) + 1}`} · ${p.rank || ""}${SUIT_SYMBOL[p.suit] || ""}`
                : row.event === "player_action" ? `Seat #${Number(p.seat) + 1} · ${String(p.action || "").toUpperCase()}`
                : row.event === "main_bet" || row.event === "side_bet" ? `${Number(p.amount || 0).toFixed(2)}`
                : p.round_number != null ? `Round #${p.round_number}` : "";
              return (
                <div key={row.id || `${row.ts}-${row.event}`} className="flex items-center justify-between gap-3 rounded-lg bg-black/25 px-3 py-2 text-xs">
                  <div className="min-w-0">
                    <span className="font-black uppercase text-white/70">{String(row.event || "").replaceAll("_", " ")}</span>
                    {row.actor_username && <span className="ml-2 text-white/30">by {row.actor_username}</span>}
                  </div>
                  <div className="shrink-0 text-right">
                    {detail && <p className="font-bold text-white/55">{detail}</p>}
                    <p className="text-[9px] text-white/25">{row.ts ? new Date(row.ts).toLocaleTimeString() : ""}</p>
                  </div>
                </div>
              );
            }) : <p className="py-3 text-center text-xs text-[#52525b]">No audit events recorded yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
